---
title: Getting Started
lede: Download, install, and walk the setup the first launch opens on.
order: 2
part: Get started
---

Download Airlock from [tryairlock.app](https://tryairlock.app). It runs for 14 days without a licence.

Airlock runs on macOS, on a Mac with Apple silicon. There is no Windows or Linux version. Micro-VMs need nothing more.

Open the `.dmg`, drag Airlock to Applications, and open it.

### What else it needs

The agent's CLI on your `PATH`: `claude`, `codex`, or both. Airlock brings everything else under `~/.airlock`, and never changes your own tools or git config.

### The first-launch setup

A fresh install opens on **Set up Airlock**. **Skip setup** is always at the top right.

1. **This computer** checks the micro-VM runtime, the OS keychain and your licence or trial.
2. **License** takes your key: **Sign in** fetches a key you already own from your account, or paste the key from your receipt and press **Use key**. No key yet? **Keep the trial** goes on, and the trial keeps running. Signing in also links the computer to your organization, if you are in one.
3. **Sign in** shows each agent. For Claude Code, press **Sign in**, approve the page in your browser and paste back its code. For Codex, run `codex login` in a terminal, then press **Check again**. An agent whose CLI is not on your `PATH` shows the command that installs it. One agent is enough; **Skip for now** goes on without one.
4. **Integrations**, if your organization uses Jira, Trello, GitHub or ngrok.
5. **Workspaces**, if your organization shares workspaces this computer does not have yet.
6. **Containers**, if a workspace you got runs services on this computer that are not running.
7. **Ready** names the settings page where each answer can be changed.

Steps 4 to 6 appear only for a member of an organization, and only when there is something to ask.

![Signing an agent in](media/setup-agents.webp)

### Integrations

Once you sign in on the **License** step, the setup asks your account what your organization uses. The **Integrations** step lists each integration, says why your account asks for it, and shows `IN PLACE` or `TO SET UP`. Each one needs a sign-in on this computer: enter yours there, through the same guided page as in **Settings**. Jira and Trello tokens are checked before they are kept, GitHub signs in through your browser, and an ngrok authtoken is checked when the first tunnel opens. **Skip this one** leaves an integration for later, in **Settings**.

Your organization may also share one Jira or Trello sign-in with its members. On a computer with no sign-in of its own, Airlock uses it. See [Integrations](/integrations).

### Workspaces

The **Workspaces** step lists the workspaces your organization shares, so there is no workspace to create from scratch. Tick the ones to get; nothing ticked is fine too, and they stay offered in **Settings › License**. Each workspace you tick then gets a page of its own: say where each repository goes and press **Clone**. The page waits until every repository is here; **Skip this one** goes on without it. See [Your organization's workspaces](/organization).

If a workspace you got uses an integration this computer has no sign-in for yet, a **Their integrations** step asks for it after the workspace pages.

### Containers

If a workspace you got declares services, such as a database, that run in your own container runner, the **Containers** step shows each one. Press **Start**, or **Recreate**, for the ones you want now. Nothing starts by itself, and **Continue** goes on whatever is running.

### Finishing

With no workspace yet, **Add your first workspace** ends the setup and opens the workspace wizard. See [Workspaces](/workspaces). If you got workspaces from your organization, **Done** ends it instead. To run the setup again: **Settings › General › First-launch setup › Run again**.

### Quitting does not stop the work

**Quit Airlock (sessions keep running)** closes the window only. Reopen Airlock and every session is where you left it.
